/*
 * Treatment-types settings page — /{pub}/site-config/modules.
 *
 * Edits the `treatmentTypes` field on publication-setting. Each entry is
 * keyed by a treatment-type name (e.g. SECTION_TOP, MOST_POPULAR, or for
 * barrons something namespaced like `legacy:TOP_STORY`).
 *
 * Storage shape (verbatim from the migration source) is nested:
 *   { label, minRequiredContentItems, maxRequiredContentItems,
 *     contentTypes, brand, requiredProperties,
 *     defaultModule: {
 *       uiModuleType, groupId, moduleItems,
 *       uiModuleFields: { <subtype>: { allowedTreatmentTypes, allowedMeta, contentTypes, ... } }
 *     } }
 *
 * The form flattens the structural fields (allowed swap types, allowed
 * metadata, module subtype's contentTypes) up to the top level for
 * editing, then reassembles the nested shape on save. Per-instance fields
 * like `title`, `titleUrl`, and `moduleItems` are NOT exposed here — those
 * are layout-level overrides edited in the layouts page.
 *
 * Save goes through publicationSettingUpdate, same mutation as the main
 * settings page.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ComboboxItem } from '@screentone/core';
import {
  Box,
  Button,
  FormHelperText,
  FormLabel,
  Grid,
  Group,
  IconPlus,
  IconSave,
  IconTrash,
  Input,
  MultiSelect,
  Select,
  Typography,
  Wrapper
} from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { Accordion } from 'components/accordion/Accordion';
import { useAlert } from 'contexts/alert/useAlert';
import {
  PublicationSettingSearchableContentType,
  PublicationSettingUpdateInput,
  UiModuleType,
  usePublicationSettingUpdateMutation
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import styles from './TreatmentTypesSettings.module.scss';

type StoredModule = {
  uiModuleType?: string;
  groupId?: string;
  moduleItems?: unknown[];
  uiModuleFields?: Record<string, Record<string, unknown>>;
} | null;

// Stored shape (canonical / what the resolver consumes).
// `module` is the primary key (wsj homepage backfill copies TT entries as-is,
// preserving the embedded `module` field from the source config).
// `defaultModule` is written by the modules-half backfill path (wsj section,
// cwsj, barrons) where mergeModulesAndTreatmentTypes attaches MODULES[key] as
// `defaultModule`. Both keys carry the same shape; read module ?? defaultModule.
type StoredEntry = {
  label?: string;
  minRequiredContentItems?: number | null;
  maxRequiredContentItems?: number | null;
  requiredProperties?: string[];
  contentTypes?: string[];
  brand?: string;
  module?: StoredModule;
  defaultModule?: StoredModule;
  [key: string]: unknown;
};

type StoredCatalog = Record<string, StoredEntry>;

// Flat form-facing shape — what the UI edits.
type FormFields = {
  label: string;
  uiModuleType: string;
  contentTypes: string[];
  minRequiredContentItems: number | null;
  maxRequiredContentItems: number | null;
  requiredProperties: string[];
  allowedTreatmentTypes: string[];
  allowedMeta: string[];
  treatmentTypeKey: string;
  ignoreBaseQuery: boolean | null;
  skipContentParameters: boolean | null;
  optional: boolean | null;
};

const ALL_CONTENT_TYPES = Object.values(PublicationSettingSearchableContentType) as string[];
const UI_MODULE_TYPES = Object.values(UiModuleType);
const ALL_METADATA = ['title', 'titleUrl', 'subtitle', 'subtitleUrl'];

/**
 * Map a UiModuleType enum value to the `uiModuleFields` subtype key it
 * uses (e.g. UiTitleModuleType → 'titleModule'). The catalog source
 * always uses lowercased-first-letter form of the type name minus the
 * trailing "Type" suffix.
 */
function uiModuleFieldsKeyFor(uiModuleType: string | undefined): string {
  if (!uiModuleType) return 'titleModule';
  // 'UiTitleModuleType' -> 'titleModule'
  // 'UiBasicOptionalModuleType' -> 'basicOptionalModule'
  const stripped = uiModuleType.replace(/^Ui/, '').replace(/Type$/, '');
  return stripped.charAt(0).toLowerCase() + stripped.slice(1);
}

function entryToFormFields(entry: StoredEntry): FormFields {
  const dm = entry.module ?? entry.defaultModule ?? {};
  const subtypeKey = uiModuleFieldsKeyFor(dm.uiModuleType);
  const subtype = dm.uiModuleFields?.[subtypeKey] ?? {};

  return {
    label: typeof entry.label === 'string' ? entry.label : '',
    uiModuleType: dm.uiModuleType ?? '',
    contentTypes: Array.isArray(subtype.contentTypes)
      ? (subtype.contentTypes as string[])
      : Array.isArray(entry.contentTypes)
        ? entry.contentTypes
        : [],
    minRequiredContentItems: entry.minRequiredContentItems ?? null,
    maxRequiredContentItems: entry.maxRequiredContentItems ?? null,
    requiredProperties: entry.requiredProperties ?? [],
    allowedTreatmentTypes: Array.isArray(subtype.allowedTreatmentTypes)
      ? (subtype.allowedTreatmentTypes as string[])
      : [],
    allowedMeta: Array.isArray(subtype.allowedMeta) ? (subtype.allowedMeta as string[]) : [],
    treatmentTypeKey: typeof entry.treatmentTypeKey === 'string' ? entry.treatmentTypeKey : '',
    ignoreBaseQuery: typeof entry.ignoreBaseQuery === 'boolean' ? entry.ignoreBaseQuery : null,
    skipContentParameters: typeof entry.skipContentParameters === 'boolean' ? entry.skipContentParameters : null,
    optional: typeof entry.optional === 'boolean' ? entry.optional : null
  };
}

/**
 * Reverse the flatten — write top-level form fields back into the nested
 * stored shape. Preserves any unrecognized fields on the original entry
 * and on the inner subtype object so we don't drop data the form doesn't
 * surface (title, titleUrl, treatmentType-self-reference, etc.).
 */
function formFieldsToEntry(form: FormFields, original: StoredEntry, key: string): StoredEntry {
  const dm = original.module ?? original.defaultModule ?? {};
  const subtypeKey = uiModuleFieldsKeyFor(form.uiModuleType || dm.uiModuleType);
  const existingSubtype = dm.uiModuleFields?.[subtypeKey] ?? {};

  const nextSubtype = {
    ...existingSubtype,
    treatmentType: existingSubtype.treatmentType ?? key,
    allowedTreatmentTypes: form.allowedTreatmentTypes,
    allowedMeta: form.allowedMeta,
    contentTypes: form.contentTypes
  };

  return {
    ...original,
    label: form.label || undefined,
    minRequiredContentItems: form.minRequiredContentItems,
    maxRequiredContentItems: form.maxRequiredContentItems,
    requiredProperties: form.requiredProperties,
    contentTypes: form.contentTypes,
    treatmentTypeKey: form.treatmentTypeKey || undefined,
    ignoreBaseQuery: form.ignoreBaseQuery ?? undefined,
    skipContentParameters: form.skipContentParameters ?? undefined,
    optional: form.optional ?? undefined,
    defaultModule: {
      ...dm,
      uiModuleType: form.uiModuleType || dm.uiModuleType,
      uiModuleFields: {
        ...(dm.uiModuleFields ?? {}),
        [subtypeKey]: nextSubtype
      }
    }
  };
}

const EMPTY_FORM: FormFields = {
  label: '',
  uiModuleType: '',
  contentTypes: [],
  minRequiredContentItems: null,
  maxRequiredContentItems: null,
  requiredProperties: [],
  allowedTreatmentTypes: [],
  allowedMeta: [],
  treatmentTypeKey: '',
  ignoreBaseQuery: null,
  skipContentParameters: null,
  optional: null
};

const TreatmentTypesSettings = () => {
  const { property } = useParams<{ property: string }>();
  const currentProperty = useConvertedProperty();
  const pubKey = property ?? currentProperty ?? '';

  const { data, isLoading, fetchStatus } = usePublicationSettings({ publicationKey: pubKey });
  const isFetching = isLoading && fetchStatus !== 'idle';

  const { alertError, alertSuccess } = useAlert();
  const { mutateAsync, isLoading: isSaving } = usePublicationSettingUpdateMutation();

  // Persisted catalog. Always reflects what's in DB (or what we last saved).
  const [catalog, setCatalog] = useState<StoredCatalog>({});

  // Editing state — separate from the persisted catalog so the user can
  // discard changes by switching rows / clicking Cancel.
  // `editingKey === null` means nothing is being edited (no form).
  // `isNew === true` means the editingKey isn't in the catalog yet — only
  // committed on Save.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [draft, setDraft] = useState<FormFields>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormFields | 'key', string>>>({});

  useEffect(() => {
    if (data?.publicationSetting.treatmentTypes) {
      setCatalog(data.publicationSetting.treatmentTypes as StoredCatalog);
    } else {
      setCatalog({});
    }
  }, [data]);

  const sortedKeys = useMemo(() => Object.keys(catalog).sort(), [catalog]);

  // Compute whether the current draft differs from what's persisted. For
  // new entries, "dirty" means anything has been entered.
  const persistedFormFields: FormFields | undefined = useMemo(() => {
    if (!editingKey || isNew) return undefined;
    const entry = catalog[editingKey] as StoredEntry | undefined;
    return entry ? entryToFormFields(entry) : undefined;
  }, [editingKey, isNew, catalog]);

  const isDirty = useMemo(() => {
    if (!editingKey) return false;
    if (isNew) {
      // A new draft is dirty as soon as any field differs from EMPTY_FORM.
      return JSON.stringify(draft) !== JSON.stringify(EMPTY_FORM);
    }
    return JSON.stringify(draft) !== JSON.stringify(persistedFormFields ?? EMPTY_FORM);
  }, [draft, editingKey, isNew, persistedFormFields]);

  const updateField = <K extends keyof FormFields>(field: K, value: FormFields[K]) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    // Clear any error on the field as soon as the user touches it.
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const formBoxRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => formBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const openEdit = (key: string) => {
    if (editingKey === key) return;
    if (isDirty) {
      alertError(`Discarded unsaved changes to "${editingKey ?? ''}".`);
    }
    const entry = catalog[key] as StoredEntry | undefined;
    setEditingKey(key);
    scrollToForm();
    setIsNew(false);
    setDraft(entry ? entryToFormFields(entry) : EMPTY_FORM);
    setErrors({});
  };

  const handleAddEntry = () => {
    if (isDirty) {
      alertError(`Discarded unsaved changes to "${editingKey ?? ''}".`);
    }
    // Open an empty draft with no key yet. The user types the key in the
    // form's first input. The entry is committed to the catalog only on
    // Save (validateDraft enforces the key is non-empty + unique).
    setEditingKey('');
    setIsNew(true);
    setDraft(EMPTY_FORM);
    setErrors({});
    scrollToForm();
  };

  const handleCancel = () => {
    // Always close the form — for new entries this discards them entirely;
    // for existing entries it discards any in-progress edits and returns to
    // the placeholder. The user can re-open the entry by clicking it again.
    setEditingKey(null);
    setIsNew(false);
    setDraft(EMPTY_FORM);
    setErrors({});
  };

  const validateDraft = (): boolean => {
    const next: typeof errors = {};
    if (!editingKey?.trim()) {
      next.key = 'Key is required.';
    } else if (isNew && (catalog[editingKey] as StoredEntry | undefined) !== undefined) {
      next.key = `Key "${editingKey}" already exists.`;
    }
    if (!draft.uiModuleType) next.uiModuleType = 'Module type is required.';
    if (!draft.label.trim()) next.label = 'Display label is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (editingKey === null || !data?.publicationSetting || !pubKey) return;
    if (!validateDraft()) {
      alertError('Fix the highlighted fields before saving.');
      return;
    }

    // Build the next catalog: commit draft → catalog, then catalog → mutation.
    const original = (catalog[editingKey] as StoredEntry | undefined) ?? {};
    const nextEntry = formFieldsToEntry(draft, original, editingKey);
    const nextCatalog: StoredCatalog = { ...catalog, [editingKey]: nextEntry };

    const existing = data.publicationSetting;
    const input: PublicationSettingUpdateInput = {
      ...existing,
      treatmentTypes: nextCatalog
    };

    try {
      await mutateAsync({ publicationSettingUpdateInput: input });
      setCatalog(nextCatalog);
      setIsNew(false);
      alertSuccess(`Saved "${editingKey}".`);
    } catch (err) {
      alertError(`Failed to save: ${(err as Error).message}`);
    }
  };

  if (isFetching) return <ResponsiveLoader size="xl" />;

  // All keys in the current catalog become valid swap-type options.
  const swapTypeOptions = sortedKeys.filter((k) => k !== editingKey);

  return (
    <Wrapper margin={{ top: 'xl' }}>
      <Typography margin={{ bottom: 'sm' }} variant="note">
        Define the treatment types available on this publication — their display labels, module shapes, allowed swaps,
        and content rules.
      </Typography>
      <Wrapper margin={{ bottom: 'lg' }}>
        <Accordion title="What is a treatment type?">
          <Typography variant="bodytext">
            A <strong>treatment type</strong> is an identifier attached to every module on a page — it tells the site
            how that module should look (e.g. <em>HERO_1_LARGE_IMAGE</em> for a full-width lead, <em>MORE_LIST</em> for
            a plain headline list, <em>MOST_POPULAR</em> for a numbered ranking). Each entry here defines one treatment
            type for this publication: its display label in the curation tool, which module shape it uses, which other
            treatment types layouts can swap it for, and which content types it accepts. Editors pick from this catalog
            when placing modules on a page.
          </Typography>
        </Accordion>
      </Wrapper>

      <Grid fluid>
        <Grid.Row>
          {/* List column. Plain text-button list, no per-row actions. */}
          <Grid.Col xs={12} sm={12} md={5} lg={5}>
            <Box>
              <Box.Title>Catalog ({sortedKeys.length})</Box.Title>
              <Box.Content className={styles.keyListContent}>
                <Group gap="xs" direction="column" fullWidth>
                  <Button tertiary icon={IconPlus} onClick={handleAddEntry}>
                    Add entry
                  </Button>
                  {sortedKeys.length === 0 && (
                    <Typography variant="note">No entries yet. Click &quot;Add entry&quot; to create one.</Typography>
                  )}
                  {sortedKeys.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={`${styles.keyRow} ${editingKey === key ? styles.keyRowSelected : ''}`}
                      onClick={() => openEdit(key)}
                    >
                      {key}
                    </button>
                  ))}
                </Group>
              </Box.Content>
            </Box>
          </Grid.Col>

          {/* Form column. */}
          <Grid.Col xs={12} sm={12} md={7} lg={7}>
            <div ref={formBoxRef}>
              <Box>
                <Box.Title>
                  {editingKey === null
                    ? 'Select a treatment type'
                    : isNew
                      ? `New treatment type${editingKey ? `: ${editingKey}` : ''}`
                      : `Edit: ${editingKey}`}
                </Box.Title>
                <Box.Content>
                  {editingKey !== null ? (
                    <Group gap="md" direction="column" fullWidth>
                      {isNew && (
                        <Group gap="xs" direction="column" fullWidth>
                          <FormLabel label="Key" labelPosition="top" required />
                          <FormHelperText>
                            Used to reference this treatment type from layouts (e.g. SECTION_TOP, MOST_POPULAR).
                            Letters, numbers, dashes, underscores, dots, or colons. Cannot be changed after save.
                          </FormHelperText>
                          <Input
                            placeholder="e.g. SECTION_TOP"
                            value={editingKey}
                            onChange={(e) => {
                              // Filter as the user types — only allow characters
                              // that already appear in real catalog keys across
                              // pubs (incl. barrons' namespacing colon and the
                              // wsj ITP dot/dash keys).
                              const cleaned = e.currentTarget.value.replace(/[^a-zA-Z0-9_\-:.]/g, '');
                              setEditingKey(cleaned);
                              if (errors.key) setErrors((prev) => ({ ...prev, key: undefined }));
                            }}
                            error={!!errors.key}
                            autoFocus
                          />
                          {errors.key && <FormHelperText error>{errors.key}</FormHelperText>}
                        </Group>
                      )}

                      <Group gap="xs" direction="column" fullWidth>
                        <FormLabel label="Display label" labelPosition="top" required />
                        <Typography variant="note">
                          Human-readable name shown in editor UIs (e.g. the treatment-type dropdown when editing a
                          module). Not the rendered title that appears on the published site.
                        </Typography>
                        <Input
                          placeholder="e.g. 'Module', 'Top Stories'"
                          value={draft.label}
                          onChange={(e) => updateField('label', e.currentTarget.value)}
                          error={!!errors.label}
                        />
                        {errors.label && <FormHelperText error>{errors.label}</FormHelperText>}
                      </Group>

                      <Group gap="md" direction="row" fullWidth>
                        <Group gap="xs" direction="column" fullWidth>
                          <FormLabel label="Module type" labelPosition="top" required />
                          <Select
                            value={draft.uiModuleType}
                            onChange={(e) => updateField('uiModuleType', e.currentTarget.value)}
                            error={!!errors.uiModuleType}
                          >
                            <option value="">(pick a module type)</option>
                            {UI_MODULE_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </Select>
                          {errors.uiModuleType && <FormHelperText error>{errors.uiModuleType}</FormHelperText>}
                        </Group>
                      </Group>

                      <Group gap="xs" direction="column" fullWidth>
                        <MultiSelect
                          label="Allowed treatment types"
                          items={swapTypeOptions.map((k) => ({ id: k, label: k, value: k }))}
                          selectedItems={draft.allowedTreatmentTypes.map((k) => ({ id: k, label: k, value: k }))}
                          onSelectedItemsChange={(
                            items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])
                          ) => {
                            const current = draft.allowedTreatmentTypes.map((k) => ({ id: k, label: k, value: k }));
                            const resolved = typeof items === 'function' ? items(current) : items;
                            updateField(
                              'allowedTreatmentTypes',
                              resolved.map((i) => i.value as string)
                            );
                          }}
                          resetPosition="inside"
                          placeholder="Pick treatment types layouts can switch this module to"
                        />
                        <Typography variant="note">
                          Other treatment types layouts can switch this module to (the underlying
                          `allowedTreatmentTypes` field). Empty = swaps disallowed.
                        </Typography>
                      </Group>

                      <Group gap="xs" direction="column" fullWidth>
                        <MultiSelect
                          label="Allowed metadata fields"
                          items={ALL_METADATA.map((m) => ({ id: m, label: m, value: m }))}
                          selectedItems={draft.allowedMeta.map((m) => ({ id: m, label: m, value: m }))}
                          onSelectedItemsChange={(
                            items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])
                          ) => {
                            const current = draft.allowedMeta.map((m) => ({ id: m, label: m, value: m }));
                            const resolved = typeof items === 'function' ? items(current) : items;
                            updateField(
                              'allowedMeta',
                              resolved.map((i) => i.value as string)
                            );
                          }}
                          resetPosition="inside"
                          placeholder="Pick which fields layouts can override"
                        />
                        <Typography variant="note">
                          Which per-layout overrides are allowed (title, titleUrl, etc.).
                        </Typography>
                      </Group>

                      <Group gap="xs" direction="column" fullWidth>
                        <MultiSelect
                          label="Content types"
                          items={ALL_CONTENT_TYPES.map((ct) => ({ id: ct, label: ct, value: ct }))}
                          selectedItems={draft.contentTypes.map((ct) => ({ id: ct, label: ct, value: ct }))}
                          onSelectedItemsChange={(
                            items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])
                          ) => {
                            const current = draft.contentTypes.map((ct) => ({ id: ct, label: ct, value: ct }));
                            const resolved = typeof items === 'function' ? items(current) : items;
                            updateField(
                              'contentTypes',
                              resolved.map((i) => i.value as string)
                            );
                          }}
                          resetPosition="inside"
                          placeholder="Pick allowed content types"
                        />
                      </Group>

                      <Group gap="md" direction="row" fullWidth>
                        <Group gap="xs" direction="column" fullWidth>
                          <FormLabel label="Min items" labelPosition="top" />
                          <Input
                            type="number"
                            min={0}
                            value={draft.minRequiredContentItems ?? ''}
                            onChange={(e) => {
                              const n = Number(e.currentTarget.value);
                              updateField(
                                'minRequiredContentItems',
                                e.currentTarget.value === '' || Number.isNaN(n) ? null : Math.max(0, n)
                              );
                            }}
                          />
                        </Group>
                        <Group gap="xs" direction="column" fullWidth>
                          <FormLabel label="Max items" labelPosition="top" />
                          <Input
                            type="number"
                            min={0}
                            value={draft.maxRequiredContentItems ?? ''}
                            onChange={(e) => {
                              const n = Number(e.currentTarget.value);
                              updateField(
                                'maxRequiredContentItems',
                                e.currentTarget.value === '' || Number.isNaN(n) ? null : Math.max(0, n)
                              );
                            }}
                          />
                        </Group>
                      </Group>

                      <Group gap="xs" direction="column" fullWidth>
                        <MultiSelect
                          label="Required properties"
                          items={ALL_METADATA.map((m) => ({ id: m, label: m, value: m }))}
                          selectedItems={draft.requiredProperties.map((m) => ({ id: m, label: m, value: m }))}
                          onSelectedItemsChange={(
                            items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])
                          ) => {
                            const current = draft.requiredProperties.map((m) => ({ id: m, label: m, value: m }));
                            const resolved = typeof items === 'function' ? items(current) : items;
                            updateField(
                              'requiredProperties',
                              resolved.map((i) => i.value as string)
                            );
                          }}
                          resetPosition="inside"
                          placeholder="Pick metadata fields that must be filled before publish"
                        />
                        <Typography variant="note">
                          Publish-validation blocks if any of these are empty on a layout module of this treatment type.
                        </Typography>
                      </Group>

                      <Group gap="xs" direction="column" fullWidth>
                        <FormLabel label="Treatment type key" labelPosition="top" />
                        <Typography variant="note">
                          When the frontend key differs from the backend enum value. Overrides the main key as the
                          module&apos;s ref identifier.
                        </Typography>
                        <Input
                          placeholder="e.g. SECTION_TOP (leave blank if same as key)"
                          value={draft.treatmentTypeKey}
                          onChange={(e) => updateField('treatmentTypeKey', e.currentTarget.value)}
                        />
                      </Group>

                      <Group gap="md" direction="row" fullWidth>
                        <Group gap="xs" direction="column" fullWidth>
                          <FormLabel label="Ignore base query" labelPosition="top" />
                          <Select
                            value={draft.ignoreBaseQuery === null ? '' : String(draft.ignoreBaseQuery)}
                            onChange={(e) => {
                              const v = e.currentTarget.value;
                              updateField('ignoreBaseQuery', v === '' ? null : v === 'true');
                            }}
                          >
                            <option value="">(unset)</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </Select>
                          <Typography variant="note">Skip the publication&apos;s base query for this type.</Typography>
                        </Group>
                        <Group gap="xs" direction="column" fullWidth>
                          <FormLabel label="Skip content parameters" labelPosition="top" />
                          <Select
                            value={draft.skipContentParameters === null ? '' : String(draft.skipContentParameters)}
                            onChange={(e) => {
                              const v = e.currentTarget.value;
                              updateField('skipContentParameters', v === '' ? null : v === 'true');
                            }}
                          >
                            <option value="">(unset)</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </Select>
                          <Typography variant="note">Omit content_parameters serialization.</Typography>
                        </Group>
                        <Group gap="xs" direction="column" fullWidth>
                          <FormLabel label="Optional" labelPosition="top" />
                          <Select
                            value={draft.optional === null ? '' : String(draft.optional)}
                            onChange={(e) => {
                              const v = e.currentTarget.value;
                              updateField('optional', v === '' ? null : v === 'true');
                            }}
                          >
                            <option value="">(unset)</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </Select>
                          <Typography variant="note">Whether this treatment type is optional.</Typography>
                        </Group>
                      </Group>
                    </Group>
                  ) : (
                    <Typography variant="note">Pick a treatment type from the list to edit.</Typography>
                  )}
                </Box.Content>
                {editingKey && (
                  <Group direction="row" align="space-between" margin={{ all: 'md' }}>
                    {/* TODO(FE-7): enable Delete once the TT blast-radius warning
                      ships and we can detect whether the treatment type is in
                      use by any layout. Until then, deleting risks breaking
                      layouts that reference this entry, so we hide it. */}
                    <Button
                      tertiary
                      icon={IconTrash}
                      color="lava"
                      disabled
                      title="Delete is disabled until layouts are migrated to the new ref/override shape (ticket 2.4)."
                    >
                      Delete (disabled)
                    </Button>
                    <Group direction="row" gap="md">
                      <Button secondary onClick={handleCancel} disabled={isSaving}>
                        Cancel
                      </Button>
                      <Button primary icon={IconSave} onClick={handleSave} disabled={isSaving || !isDirty}>
                        {isSaving ? 'Saving…' : 'Save'}
                      </Button>
                    </Group>
                  </Group>
                )}
              </Box>
            </div>
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </Wrapper>
  );
};

export default TreatmentTypesSettings;
